import * as Yup from 'yup';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useEffect } from 'react';
import { Box, Dialog, Typography, Button, DialogTitle, DialogContent } from '@mui/material';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import { IApiRequests, useApiRequests } from 'hooks';
import { PasswordUpdateRequest } from 'types';
import { FormInput, ModalTitle } from 'components';
import { withOutsideClickPreventer } from 'utils';

import styles from './Password.module.scss';

type PasswordPopupProps = {
  open: boolean;
  handleClose: () => void;
};

export const PasswordPopup = (props: PasswordPopupProps) => {
  const { t } = useTranslation('settings');
  const { updateUserPassword }: IApiRequests = useApiRequests();
  const { enqueueSnackbar } = useSnackbar();
  const emptyValues: PasswordUpdateRequest = {
    oldPassword: '',
    newPassword: '',
    newPasswordConfirmation: '',
  };
  const schema = Yup.object().shape({
    oldPassword: Yup.string().required(t('passwordPopup.passwordRequired')),
    newPassword: Yup.string()
      .min(8, t('passwordPopup.passwordMinRequirements'))
      .minLowercase(1, t('passwordPopup.passwordMinLowercase'))
      .minUppercase(1, t('passwordPopup.passwordMinUppercase'))
      .minNumbers(1, t('passwordPopup.passwordMinNumbers'))
      .minSymbols(1, t('passwordPopup.passwordMinSymbols'))
      .required(t('passwordPopup.passwordRequired')),
    newPasswordConfirmation: Yup.string().oneOf([Yup.ref('newPassword')], t('passwordPopup.passwordsMustMatch')),
  });

  const form = useForm<PasswordUpdateRequest>({
    resolver: yupResolver(schema),
    defaultValues: emptyValues,
    mode: 'all',
  });

  const resetPasswordForm = (): void => {
    form.reset(emptyValues);
  };

  const handleClose = (): void => {
    resetPasswordForm();
    props.handleClose();
  };

  useEffect(() => {
    if (!props.open) {
      resetPasswordForm();
    }
  }, [props.open]);

  const handleChangePassword = (): void => {
    const formValues = form.getValues();

    updateUserPassword(formValues)
      .then(() => {
        enqueueSnackbar(t('passwordPopup.passwordChanged'), { variant: 'success' });
        handleClose();
      })
      .catch((error) => {
        enqueueSnackbar(error?.message || t('GENERIC_ERROR', { ns: 'errors' }), { variant: 'error' });
      });
  };

  return (
    <Dialog className={styles.dialog} open={props.open} onClose={withOutsideClickPreventer(handleClose)}>
      <DialogTitle>
        <ModalTitle title={t('passwordPopup.changePasswordUpper')} onClose={handleClose} />
      </DialogTitle>

      <DialogContent>
        <Box
          component='form'
          onSubmit={(event) => {
            event.preventDefault();
            handleChangePassword();
          }}
        >
          <input
            type='text'
            name='username'
            autoComplete='username'
            value=''
            readOnly
            aria-hidden='true'
            tabIndex={-1}
            style={{ position: 'absolute', opacity: 0, height: 0, width: 0, pointerEvents: 'none' }}
          />
          <Box>
            <Typography variant='body2' className={styles.typography}>
              {t('passwordPopup.currentPassword')} <span className={styles.asterisk}>*</span>
            </Typography>
            <FormInput name='oldPassword' control={form.control} type='password' autoComplete='current-password' />
          </Box>

          <Box>
            <Typography variant='body2' className={styles.typography}>
              {t('passwordPopup.newPassword')} <span className={styles.asterisk}>*</span>
            </Typography>
            <FormInput name='newPassword' control={form.control} type='password' autoComplete='new-password' />
          </Box>

          <Box>
            <Typography variant='body2' className={styles.typography}>
              {t('passwordPopup.confirmNewPassword')} <span className={styles.asterisk}>*</span>
            </Typography>
            <FormInput
              name='newPasswordConfirmation'
              control={form.control}
              type='password'
              autoComplete='new-password'
            />
          </Box>

          <Button variant='contained' className={styles.button} type='submit' disabled={!form.formState.isValid}>
            {t('passwordPopup.changePassword')}
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};
